import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

const HowWeWorkSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Detect mobile on mount
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);

    const handleScroll = () => {
      if (!sectionRef.current) return;

      // Skip scroll calculations on mobile
      if (window.innerWidth < 1024) {
        setScrollProgress(0);
        return;
      }

      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const sectionHeight = rect.height;

      // Calculate scroll progress through the section - DESKTOP ONLY
      const start = rect.top - windowHeight;
      const end = rect.bottom;
      const total = sectionHeight + windowHeight;
      const current = -start;
      const progress = Math.max(0, Math.min(1, current / total));

      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const services = [
    {
      title: 'Growth Bottleneck Audit',
      description: 'Identify structural constraints limiting growth.',
      delay: 0,
    },
    {
      title: 'Growth Bottleneck Map',
      description: 'See where growth is blocked across positioning, pricing, narrative, and market focus.',
      delay: 0.08,
    },
    {
      title: 'Bottleneck Removal Strategy',
      description: 'Redesign the business to remove limits.',
      delay: 0.16,
    },
    {
      title: 'Force Multipliers',
      description: 'Once bottlenecks are removed, growth compounds.',
      delay: 0.24,
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="relative py-16 md:py-24 lg:py-48 px-6 md:px-8 overflow-hidden"
    >
      {/* Background ambient glow */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-slate-950 to-black"></div>
      
      {/* DESKTOP - Scroll-animated glow */}
      <div
        className="hidden lg:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-cyan-500/5 rounded-full blur-[120px] pointer-events-none"
        style={{
          opacity: scrollProgress * 0.6,
        }}
      ></div>

      {/* MOBILE - Static glow */}
      <div className="lg:hidden absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-cyan-500/5 rounded-full blur-[120px] pointer-events-none opacity-30"></div>

      <div className="relative max-w-7xl mx-auto">
        {/* DESKTOP - Scroll-animated Section Header */}
        <div className="hidden lg:block text-center mb-20 md:mb-24">
          <h2
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6"
            style={{
              opacity: isMobile ? 1 : Math.min(1, scrollProgress * 2),
              transform: isMobile ? 'translateY(0)' : `translateY(${(1 - Math.min(1, scrollProgress * 2)) * 30}px)`,
            }}
          >
            Services
          </h2>
          <p
            className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto"
            style={{
              opacity: isMobile ? 1 : Math.min(1, (scrollProgress - 0.1) * 2),
              transform: isMobile ? 'translateY(0)' : `translateY(${(1 - Math.min(1, (scrollProgress - 0.1) * 2)) * 20}px)`,
            }}
          >
            Identify and remove the structural constraints limiting your growth
          </p>
        </div>

        {/* MOBILE - Simple fade-in Section Header */}
        <div className="lg:hidden text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Services
          </h2>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto">
            Identify and remove the structural constraints limiting your growth
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {services.map((service, index) => {
            // DESKTOP - Scroll-based animation
            const cardProgress = isMobile ? 1 : Math.max(0, Math.min(1, (scrollProgress - service.delay) * 2.5));
            
            return (
              <div
                key={index}
                className="group relative"
                style={{
                  opacity: isMobile ? 1 : cardProgress,
                  transform: isMobile 
                    ? 'translateY(0) scale(1)' 
                    : `translateY(${(1 - cardProgress) * 40}px) scale(${0.95 + cardProgress * 0.05})`,
                  transition: 'transform 0.3s ease',
                }}
              >
                {/* Card glow on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/0 to-blue-500/0 group-hover:from-cyan-500/10 group-hover:to-blue-500/10 rounded-2xl blur-xl transition-all duration-500 opacity-0 group-hover:opacity-100"></div>
                
                {/* Card */}
                <div className="relative bg-slate-900/50 backdrop-blur-sm border border-slate-800 group-hover:border-cyan-500/30 rounded-2xl p-7 md:p-10 transition-all duration-300 cursor-pointer h-full">
                  {/* Number indicator */}
                  <div className="flex items-center justify-center w-11 h-11 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6">
                    <span className="text-cyan-400 font-bold text-lg">{index + 1}</span>
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 group-hover:text-cyan-400 transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 text-base md:text-lg leading-relaxed">
                    {service.description}
                  </p>

                  {/* Decorative corner accent */}
                  <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-cyan-500/5 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* DESKTOP - Scroll-animated Bottom CTA hint */}
        <div
          className="hidden lg:block text-center mt-16 md:mt-20"
          style={{
            opacity: isMobile ? 1 : Math.min(1, (scrollProgress - 0.5) * 2),
          }}
        >
          <p className="text-gray-500 text-sm md:text-base">
            Each service builds on the previous to systematically remove bottlenecks
          </p>
        </div>

        {/* MOBILE - Static Bottom CTA hint */}
        <div className="lg:hidden text-center mt-12">
          <p className="text-gray-500 text-sm md:text-base">
            Each service builds on the previous to systematically remove bottlenecks
          </p>
        </div>
      </div>
    </section>
  );
};

export default HowWeWorkSection;